import React, { useMemo, useState, useEffect } from "react";
import {
  BsFillArrowLeftCircleFill,
  BsChevronDown,
  BsList,
  BsX,
} from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  toggleJobsheet,
  toggleOrders,
  toggleQuote,
  toggleStore,
  toggleDocs,
  toggleDiecut,
  toggleProd,
  toggleUser,
  toggleReport,
} from "../features/PageSlice";
import { setPermissions } from "../features/authSlice";

import logo from "../elkom.png";
import { NavLink } from "react-router-dom";
import LoginStatus from "../components/LoginStatus";
import NotificationBell from "../components/NotificationBell";
import { MENU } from "../pages/menuItems";

const SECTION_TOGGLES = {
  quotation: { openFlag: "subQuote", action: toggleQuote },
  jobsheet: { openFlag: "subJobsheet", action: toggleJobsheet },
  orders: { openFlag: "subOrders", action: toggleOrders },
  documents: { openFlag: "subDocs", action: toggleDocs },
  diecut: { openFlag: "subDiecut", action: toggleDiecut },
  production: { openFlag: "subProd", action: toggleProd },
  store: { openFlag: "subStore", action: toggleStore },
  user: { openFlag: "subUser", action: toggleUser },
  reporting: { openFlag: "subReport", action: toggleReport },
};

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const { user, permissions } = useSelector((state) => state.auth);
  const pageState = useSelector((state) => state.pages);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  useEffect(() => {
    if (!user || permissions !== null) return;
    if (user.user_lvl === 1) {
      dispatch(setPermissions("admin"));
      return;
    }
    axios
      .get(
        `https://eps.elkomprintama.com/get/api/users/permissions/${user.user_lvl}`,
      )
      .then((res) => dispatch(setPermissions(res.data.permissions)))
      .catch(() => dispatch(setPermissions([])));
  }, [user, permissions, dispatch]);

  const permSet = useMemo(() => {
    if (!permissions || permissions === "admin") return null;
    return new Set(permissions);
  }, [permissions]);

  const canSee = (item) => {
    if (!user) return false;
    if (user.user_lvl === 1 || permissions === "admin") return true;
    if (permissions === null) return false;
    if (item.children) return item.children.some((ch) => permSet?.has(ch.key));
    return permSet?.has(item.key) ?? false;
  };

  const visibleMenu = useMemo(
    () => MENU.filter((m) => canSee(m)),
    [user, permissions, permSet],
  );

  /* ── Shared nav item renderer ───────────────────────────────────── */
  const renderNavItems = (collapsed, onNavigate) =>
    visibleMenu.map((item) => {
      const Icon = item.icon;
      const hasChildren =
        Array.isArray(item.children) && item.children.length > 0;
      const toggleInfo = SECTION_TOGGLES[item.key];
      const isOpen = toggleInfo ? pageState[toggleInfo.openFlag] : false;
      const visibleChildren = hasChildren
        ? item.children.filter((ch) => canSee(ch) && !ch.action)
        : [];

      return (
        <li key={item.key}>
          {!hasChildren && item.to ? (
            <NavLink
              to={item.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 mx-2 my-0.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-blue-600 text-white font-semibold"
                    : "text-gray-300 hover:bg-gray-700 hover:text-white"
                }`
              }
            >
              {Icon && <Icon size="1.2em" className="flex-shrink-0" />}
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ) : (
            <div
              className="flex items-center gap-3 px-3 py-2.5 mx-2 my-0.5 rounded-md text-sm text-gray-300 hover:bg-gray-700 hover:text-white cursor-pointer select-none transition-colors"
              onClick={() => toggleInfo && dispatch(toggleInfo.action())}
            >
              {Icon && <Icon size="1.2em" className="flex-shrink-0" />}
              {!collapsed && (
                <>
                  <span className="truncate flex-1">{item.label}</span>
                  <BsChevronDown
                    className={`flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                  />
                </>
              )}
            </div>
          )}

          {hasChildren && isOpen && !collapsed && (
            <ul className="ml-5 border-l border-gray-600 pl-2 mb-1">
              {visibleChildren.map((ch) => (
                <li key={ch.key}>
                  <NavLink
                    to={ch.to}
                    end
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2 my-0.5 rounded-md text-xs transition-colors ${
                        isActive
                          ? "bg-blue-600 text-white font-semibold"
                          : "text-gray-400 hover:bg-gray-700 hover:text-white"
                      }`
                    }
                  >
                    {ch.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    });

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <div
        className="text-white px-4 flex justify-between items-center shadow-lg z-30 flex-shrink-0"
        style={{
          background:
            "linear-gradient(135deg, #0f2027 0%, #203a43 60%, #2c5364 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          minHeight: "56px",
        }}
      >
        {/* Left: hamburger (mobile) + logo */}
        <div className="flex items-center gap-3">
          {/* Hamburger — mobile only */}
          <button
            className="md:hidden p-1.5 rounded-lg text-white hover:bg-white/10 active:bg-white/20 transition-colors"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <BsList size={24} />
          </button>

          <div className="bg-white/10 rounded-lg p-1 flex-shrink-0">
            <img
              src={logo}
              width="36"
              height="36"
              alt="logo"
              className="object-contain"
            />
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-tight tracking-wide">
              EPS
            </p>
            <p className="text-blue-300 text-[10px] leading-tight opacity-80 whitespace-nowrap">
              PT. Elkom Printama Batam
            </p>
          </div>
          <div className="h-6 w-px bg-white/15 ml-2 hidden sm:block" />
          <p className="text-gray-400 text-[10px] hidden sm:block">
            Elkom Integrated Management System
          </p>
        </div>

        <div className="flex items-center gap-1">
          <NotificationBell />
          <LoginStatus />
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Desktop sidebar ─────────────────────────────────────── */}
        <div
          className={`hidden md:flex bg-gray-800 flex-shrink-0 flex-col relative transition-all duration-300 ${
            sidebarOpen ? "w-56" : "w-16"
          }`}
        >
          <BsFillArrowLeftCircleFill
            className={`bg-white text-gray-800 text-2xl rounded-full absolute -right-3 top-6 cursor-pointer z-20 transition-transform duration-300 ${
              !sidebarOpen ? "rotate-180" : ""
            }`}
            onClick={() => setSidebarOpen((v) => !v)}
          />
          <ul className="pt-3 pb-6 overflow-y-auto flex-1">
            {renderNavItems(!sidebarOpen, () => {})}
          </ul>
        </div>

        {/* ── Mobile drawer overlay ────────────────────────────────── */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-40 md:hidden"
            onClick={closeMobile}
          />
        )}

        {/* ── Mobile drawer ────────────────────────────────────────── */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-72 bg-gray-800 flex flex-col md:hidden transition-transform duration-300 ease-in-out ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Drawer header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{
              background: "rgba(255,255,255,0.05)",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div className="flex items-center gap-2">
              <div className="bg-white/10 rounded-md p-1">
                <img
                  src={logo}
                  width="28"
                  height="28"
                  alt="logo"
                  className="object-contain"
                />
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-tight">
                  EPS
                </p>
                <p className="text-blue-300 text-[10px] leading-tight opacity-80">
                  PT. Elkom Printama Batam
                </p>
              </div>
            </div>
            <button
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/20 transition-colors"
              onClick={closeMobile}
              aria-label="Close menu"
            >
              <BsX size={22} />
            </button>
          </div>

          {/* Drawer nav */}
          <ul className="pt-3 pb-8 overflow-y-auto flex-1">
            {renderNavItems(false, closeMobile)}
          </ul>

          {/* Drawer footer — user info */}
          {user && (
            <div
              className="flex-shrink-0 px-4 py-3 border-t"
              style={{ borderColor: "rgba(255,255,255,0.08)" }}
            >
              <p className="text-gray-400 text-xs">Signed in as</p>
              <p className="text-white text-sm font-semibold truncate">
                {user.username}
              </p>
            </div>
          )}
        </div>

        {/* ── Main content ─────────────────────────────────────────── */}
        <div className="flex-1 bg-gray-100 overflow-auto p-4">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
